import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./PayoutHistory.module.sass";
import Card from "../../../components/Card";
import { numberWithCommas } from "../../../utils.js";
import { getTransactions } from "../../../services/orderService";

const PayoutHistory = ({ className }) => {
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayoutHistory();
  }, []);

  const fetchPayoutHistory = async () => {
    setLoading(true);

    try {
      const { data, error } = await getTransactions({ limit: 1000 });

      if (!error && data) {
        // Group transactions by month and calculate monthly payouts
        const monthlyPayouts = {};

        data.forEach(tx => {
          if (tx.type === 'payout') {
            const date = new Date(tx.created_at);
            const monthKey = `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;

            if (!monthlyPayouts[monthKey]) {
              monthlyPayouts[monthKey] = {
                date: monthKey,
                dateObj: date,
                status: tx.status === 'completed',
                method: tx.payment_method || 'Paypal',
                earnings: 0,
                amount: 0,
              };
            }

            monthlyPayouts[monthKey].earnings += tx.amount || 0;
            monthlyPayouts[monthKey].amount += tx.amount || 0;
          }
        });

        // Only show real payout transactions - no simulated data

        // Convert to array and sort by date (newest first)
        const sortedPayouts = Object.values(monthlyPayouts)
          .sort((a, b) => b.dateObj - a.dateObj)
          .slice(0, 12); // Show last 12 months

        setPayoutHistory(sortedPayouts);
      }
    } catch (error) {
      console.error('Error fetching payout history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card
        className={cn(styles.card, className)}
        title="Payout history"
        classTitle="title-blue"
      >
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Loading...
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn(styles.card, className)}
      title="Payout history"
      classTitle="title-blue"
    >
      <div className={styles.wrapper}>
        <div className={styles.table}>
          <div className={styles.row}>
            <div className={styles.col}>Month</div>
            <div className={styles.col}>Status</div>
            <div className={styles.col}>Method</div>
            <div className={styles.col}>Earnings</div>
            <div className={styles.col}>Amount withdrawn</div>
          </div>
          {payoutHistory.length > 0 ? (
            payoutHistory.map((x, index) => (
              <div className={styles.row} key={index}>
                <div className={styles.col}>{x.date}</div>
                <div className={styles.col}>
                  {x.status ? (
                    <div
                      className={cn(
                        { "status-green-dark": x.status === true },
                        styles.status
                      )}
                    >
                      Paid
                    </div>
                  ) : (
                    <div
                      className={cn(
                        { "status-yellow": x.status === false },
                        styles.status
                      )}
                    >
                      Pending
                    </div>
                  )}
                </div>
                <div className={styles.col}>
                  <div
                    className={cn(
                      { "status-blue": x.method === "Paypal" },
                      { "status-purple": x.method === "SWIFT" },
                      styles.status
                    )}
                  >
                    {x.method}
                  </div>
                </div>
                <div className={styles.col}>
                  ${numberWithCommas(x.earnings.toFixed(2))}
                </div>
                <div className={styles.col}>
                  ${numberWithCommas(x.amount.toFixed(2))}
                </div>
              </div>
            ))
          ) : (
            <div className={styles.row}>
              <div className={styles.col}>No payout history available</div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default PayoutHistory;
